import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Search } from "lucide-react";
import SEO from "@/components/SEO";
import ShopifyBuyButton from "@/components/ShopifyBuyButton";
import TeamSetCard from "@/components/shop/TeamSetCard";
import { fetchTeamSets } from "@/lib/shopifyStorefront";
import { getAlbumOrderIndex } from "@/lib/albumOrder";

/**
 * /shop/team-sets/:variation
 *
 * Dedicated "View All" catalog page for one Team Set variation. Wires
 * directly to the FULL Shopify collection (48 country products) via
 * `fetchTeamSets(variation, 48)`. Shopify remains the single source of
 * truth for title, price, image, availability, variant ID, and URL.
 *
 * Sort — client-side, applied AFTER search so search + sort compose.
 *   • Price: High → Low   (default)
 *   • Price: Low → High
 *   • Country: A → Z
 *   • Country: Z → A
 *   • Album Order              (canonical Panini WC 2026 section order
 *                               via `lib/albumOrder.js`)
 */
const VARIATIONS = {
  gold:  { title: "Gold Crumple Full Team Sets",   copy: "All 48 country teams in the premium Gold Crumple variation." },
  blue:  { title: "Blue Parallel Full Team Sets",  copy: "All 48 country teams in the Blue Parallel variation." },
  white: { title: "White Full Team Sets",          copy: "All 48 country teams in the regular White collection." },
};

const SORT_OPTIONS = [
  { value: "price-desc", label: "PRICE: HIGH → LOW" },
  { value: "price-asc",  label: "PRICE: LOW → HIGH" },
  { value: "name-asc",   label: "COUNTRY: A → Z" },
  { value: "name-desc",  label: "COUNTRY: Z → A" },
  { value: "album",      label: "ALBUM ORDER" },
];

function sortTeamSets(products, sort) {
  const nameOf = (p) => String(p.country || p.product_title || p.country_code || "zzz").toLowerCase();
  const priceOf = (p) => (Number.isFinite(p._price_amount) ? p._price_amount : Number.POSITIVE_INFINITY);
  const albumOf = (p) => getAlbumOrderIndex(p.country_code);
  const arr = products.slice();
  arr.sort((a, b) => {
    switch (sort) {
      case "name-asc":   return nameOf(a).localeCompare(nameOf(b));
      case "name-desc":  return nameOf(b).localeCompare(nameOf(a));
      case "price-asc":  return priceOf(a) - priceOf(b);
      case "album":      return albumOf(a) - albumOf(b) || nameOf(a).localeCompare(nameOf(b));
      case "price-desc":
      default:           return priceOf(b) - priceOf(a);
    }
  });
  return arr;
}

export default function TeamSetsCatalog() {
  const { variation } = useParams();
  const meta = VARIATIONS[variation];

  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [query,    setQuery]    = useState("");
  const [sort,     setSort]     = useState("price-desc");

  useEffect(() => {
    if (!meta) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      const res = await fetchTeamSets(variation, 48);
      if (cancelled) return;
      if (res.meta && res.meta.source === "error") {
        setError(res.meta.error || "Shopify request failed");
        setProducts([]);
      } else {
        setProducts(res.products || []);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [variation, meta]);

  const filteredSorted = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = !q ? products : products.filter((p) =>
      String(p.country || "").toLowerCase().includes(q) ||
      String(p.country_code || "").toLowerCase().includes(q) ||
      String(p.product_title || "").toLowerCase().includes(q)
    );
    return sortTeamSets(filtered, sort);
  }, [products, query, sort]);

  if (!meta) return <Navigate to="/shop" replace />;

  return (
    <div data-testid={`team-sets-catalog-${variation}`}>
      <SEO title={meta.title} description={meta.copy} />
      <section className="hero-black">
        <div className="container-gm py-12 md:py-16">
          <Link to="/shop" className="text-white/70 hover:text-white text-sm inline-flex items-center gap-1"
                data-testid="team-sets-back-to-shop">
            <ArrowLeft size={14} /> BACK TO SHOP
          </Link>
          <h1 className="h-display text-white text-4xl md:text-6xl mt-4"
              data-testid={`team-sets-catalog-title-${variation}`}>
            {meta.title.toUpperCase()}
          </h1>
          <p className="text-white/85 mt-4 max-w-2xl">
            <span data-testid={`team-sets-catalog-count-${variation}`}>
              {loading ? "Loading…" : `${products.length} ${products.length === 1 ? "country" : "countries"}`}
            </span>
            <span className="text-white/50"> · {meta.copy}</span>
          </p>
        </div>
      </section>

      <section className="container-gm py-8 md:py-12">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4 mb-6">
          <div className="flex-1 min-w-0"
               data-testid={`team-sets-search-${variation}`}>
            <div className="text-[10px] font-mono uppercase text-[var(--smoke)] mb-1">Search</div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search country (e.g. Argentina, BRA)…"
                className="w-full pl-9 pr-3 py-2 rounded-xl border-2 border-[var(--ink)]/25 focus:border-[var(--ink)] focus:outline-none text-sm bg-white"
                data-testid={`team-sets-search-input-${variation}`}
                spellCheck={false}
              />
            </div>
          </div>
          <label className="sm:w-56">
            <div className="text-[10px] font-mono uppercase text-[var(--smoke)] mb-1">Sort by</div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="w-full py-2 px-3 rounded-xl border-2 border-[var(--ink)]/25 focus:border-[var(--ink)] focus:outline-none text-sm bg-white"
              data-testid={`team-sets-sort-${variation}`}
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>

        {loading ? (
          <div className="rounded-3xl border-2 border-dashed border-[var(--ink)]/15 bg-neutral-50 p-10 md:p-14 text-center text-[var(--smoke)]"
               data-testid={`team-sets-catalog-loading-${variation}`}>
            Loading {meta.title} from Shopify…
          </div>
        ) : error ? (
          <div className="rounded-3xl border-2 border-dashed border-red-300 bg-red-50 p-10 text-center text-red-800"
               data-testid={`team-sets-catalog-error-${variation}`}>
            Could not load Team Sets from Shopify: {error}
          </div>
        ) : filteredSorted.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-[var(--ink)]/25 bg-white p-10 md:p-14 text-center"
               data-testid={`team-sets-catalog-empty-${variation}`}>
            <p className="text-lg text-[var(--smoke)] max-w-xl mx-auto">
              {query
                ? `No teams match "${query}".`
                : `No Team Sets available right now — check back soon.`}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5"
               data-testid={`team-sets-grid-${variation}`}>
            {filteredSorted.map((p) => (
              <TeamSetCard
                key={p.shopify_product_id || `${variation}-${p.country_code}`}
                product={{ ...p, variation }}
              />
            ))}
          </div>
        )}
      </section>

      {/* Hidden Shopify Buy Button — pre-warms the Shopify Buy SDK cart
          component so "Add to Cart" clicks on the catalog cards land in
          the SAME shared cart used across CollectorUp. Mirrors the
          pattern used on /shop (see Shop.jsx). */}
      <div className="sr-only" aria-hidden data-testid="shopify-buy-button-hidden">
        <ShopifyBuyButton />
      </div>
    </div>
  );
}
